import { logger } from '@/lib/logger'

export type LocationFromIP = {
  country: string | null
  countryCode: string | null
  state: string | null
  city: string | null
}

const LOCATION_CACHE_TTL_MS = 30 * 60 * 1000
const LOCATION_CACHE_REFRESH_AFTER_MS = LOCATION_CACHE_TTL_MS / 2

let cachedLocation: LocationFromIP | null | undefined
let cachedLocationExpiresAt = 0
let cachedLocationFetchedAt = 0
let inFlightLocation: Promise<LocationFromIP | null> | null = null

function normalizeLocationPayload(data: any): LocationFromIP {
  if (!data?.location) {
    // Fallback: some IP APIs return flat structure
    return {
      country: data?.country || data?.country_name || null,
      countryCode: data?.country_code || null,
      state: data?.region || data?.state || null,
      city: data?.city || null
    }
  }

  return {
    country: data.location.country || null,
    countryCode: data.location.country_code || null,
    state: data.location.state || null,
    city: data.location.city || null
  }
}

async function fetchLocationFromIP(): Promise<LocationFromIP | null> {
  if (inFlightLocation) {
    return inFlightLocation
  }

  inFlightLocation = (async () => {
    try {
      const response = await fetch('https://api.ipapi.is')
      const data = await response.json()
      logger.debug({ data }, 'Location data fetched')

      const location = normalizeLocationPayload(data)
      const now = Date.now()
      cachedLocation = location
      cachedLocationFetchedAt = now
      cachedLocationExpiresAt = now + LOCATION_CACHE_TTL_MS
      return location
    } catch (error) {
      logger.error({ error }, 'Failed to fetch location data')
      // Short negative cache so a blip doesn't stampede every send.
      const now = Date.now()
      cachedLocation = null
      cachedLocationFetchedAt = now
      cachedLocationExpiresAt = now + 60_000
      return null
    } finally {
      inFlightLocation = null
    }
  })()

  return inFlightLocation
}

export const getLocationFromIP = async (): Promise<LocationFromIP | null> => {
  const now = Date.now()
  if (cachedLocation !== undefined && cachedLocationExpiresAt > now) {
    // Serve cached value immediately; refresh in the background once stale-ish.
    if (
      cachedLocationFetchedAt > 0 &&
      now - cachedLocationFetchedAt >= LOCATION_CACHE_REFRESH_AFTER_MS &&
      !inFlightLocation
    ) {
      void fetchLocationFromIP()
    }
    return cachedLocation
  }

  return fetchLocationFromIP()
}
